import { AbstractService, ItemRelationshipDirection, } from '@standardnotes/services';
import { ItemsKeyMutator } from '@standardnotes/encryption';
import { ContentType } from '@standardnotes/domain-core';
import { TagsToFoldersMigrationApplicator } from '@Lib/Migrations/Applicators/TagsToFolders';
import { ActionsExtensionMutator, AppDataField, ComponentMutator, CreateDecryptedMutatorForItem, DecryptedPayload, DefaultAppDomain, DeleteItemMutator, FeatureRepoMutator, FillItemContent, MutationType, NoteMutator, PayloadEmitSource, PayloadsByDuplicating, PayloadTimestampDefaults, PayloadVaultOverrides, predicateFromDSLString, SmartViewDefaultIconName, TagMutator, } from '@standardnotes/models';
import { UuidGenerator, Uuids } from '@standardnotes/utils';
import { c } from 'ttag';
export class MutatorService extends AbstractService {
    constructor(itemManager, payloadManager, alerts, internalEventBus) {
        super(internalEventBus);
        this.itemManager = itemManager;
        this.payloadManager = payloadManager;
        this.alerts = alerts;
        this.internalEventBus = internalEventBus;
    }
    deinit() {
        super.deinit();
        this.itemManager = undefined;
        this.payloadManager = undefined;
    }
    /**
     * Consumers wanting to modify an item should run it through this block,
     * so that data is properly mapped through our function, and latest state
     * is properly reconciled.
     */
    async changeItem(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const results = await this.changeItems([itemToLookupUuidFor], mutate, mutationType, emitSource, payloadSourceKey);
        return results[0];
    }
    /**
     * @param mutate If not supplied, the intention would simply be to mark the item as dirty.
     */
    async changeItems(itemsToLookupUuidsFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const items = this.itemManager.findItemsIncludingBlanks(Uuids(itemsToLookupUuidsFor));
        const payloads = [];
        for (const item of items) {
            if (!item) {
                throw Error('Attempting to change non-existant item');
            }
            const mutator = CreateDecryptedMutatorForItem(item, mutationType);
            if (mutate) {
                mutate(mutator);
            }
            const payload = mutator.getResult();
            payloads.push(payload);
        }
        await this.payloadManager.emitPayloads(payloads, emitSource, payloadSourceKey);
        const results = this.itemManager.findItems(payloads.map((p) => p.uuid));
        return results;
    }
    /**
     * Run unique mutations per each item in the array, then only propagate all changes
     * once all mutations have been run. This differs from `changeItems` in that changeItems
     * runs the same mutation on all items.
     */
    async runTransactionalMutations(transactions, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const payloads = [];
        for (const transaction of transactions) {
            const item = this.itemManager.findItem(transaction.itemUuid);
            if (!item) {
                continue;
            }
            const mutator = CreateDecryptedMutatorForItem(item, transaction.mutationType || MutationType.UpdateUserTimestamps);
            transaction.mutate(mutator);
            const payload = mutator.getResult();
            payloads.push(payload);
        }
        await this.payloadManager.emitPayloads(payloads, emitSource, payloadSourceKey);
        const results = this.itemManager.findItems(payloads.map((p) => p.uuid));
        return results;
    }
    async runTransactionalMutation(transaction, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const item = this.itemManager.findSureItem(transaction.itemUuid);
        const mutator = CreateDecryptedMutatorForItem(item, transaction.mutationType || MutationType.UpdateUserTimestamps);
        transaction.mutate(mutator);
        const payload = mutator.getResult();
        await this.payloadManager.emitPayloads([payload], emitSource, payloadSourceKey);
        const result = this.itemManager.findItem(payload.uuid);
        return result;
    }
    async changeNote(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const note = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!note) {
            throw Error('Attempting to change non-existant note');
        }
        const mutator = new NoteMutator(note, mutationType);
        return this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
    }
    async changeTag(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const tag = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!tag) {
            throw Error('Attempting to change non-existant tag');
        }
        const mutator = new TagMutator(tag, mutationType);
        await this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
        return this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
    }
    async changeComponent(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const component = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!component) {
            throw Error('Attempting to change non-existant component');
        }
        const mutator = new ComponentMutator(component, mutationType);
        await this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
        return this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
    }
    async changeFeatureRepo(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const repo = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!repo) {
            throw Error('Attempting to change non-existant repo');
        }
        const mutator = new FeatureRepoMutator(repo, mutationType);
        await this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
        return this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
    }
    async changeActionsExtension(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const extension = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!extension) {
            throw Error('Attempting to change non-existant extension');
        }
        const mutator = new ActionsExtensionMutator(extension, mutationType);
        await this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
        return this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
    }
    async changeItemsKey(itemToLookupUuidFor, mutate, mutationType = MutationType.UpdateUserTimestamps, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        const itemsKey = this.itemManager.findItem(itemToLookupUuidFor.uuid);
        if (!itemsKey) {
            throw Error('Attempting to change non-existant itemsKey');
        }
        const mutator = new ItemsKeyMutator(itemsKey, mutationType);
        await this.applyTransform(mutator, mutate, emitSource, payloadSourceKey);
        return this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
    }
    async applyTransform(mutator, mutate, emitSource = PayloadEmitSource.LocalChanged, payloadSourceKey) {
        mutate(mutator);
        const payload = mutator.getResult();
        return this.payloadManager.emitPayload(payload, emitSource, payloadSourceKey);
    }
    /**
     * Sets the item as needing sync. The item is then run through the mapping function,
     * and propagated to mapping observers.
     * @param isUserModified - Whether to update the item's "user modified date"
     */
    async setItemDirty(itemToLookupUuidFor, isUserModified = false) {
        const result = await this.setItemsDirty([itemToLookupUuidFor], isUserModified);
        return result[0];
    }
    async setItemsDirty(itemsToLookupUuidsFor, isUserModified = false) {
        return this.changeItems(itemsToLookupUuidsFor, undefined, isUserModified ? MutationType.UpdateUserTimestamps : MutationType.NoUpdateUserTimestamps);
    }
    /**
     * Duplicates an item and maps it, thus propagating the item to observers.
     * @param isConflict - Whether to mark the duplicate as a conflict of the original.
     */
    async duplicateItem(itemToLookupUuidFor, isConflict = false, additionalContent) {
        const item = this.itemManager.findSureItem(itemToLookupUuidFor.uuid);
        const payload = item.payload.copy();
        const resultingPayloads = PayloadsByDuplicating({
            payload,
            baseCollection: this.payloadManager.getMasterCollection(),
            isConflict,
            additionalContent: {
                appData: {
                    [DefaultAppDomain]: {
                        [AppDataField.UserModifiedDate]: new Date(),
                    },
                },
                ...additionalContent,
            },
        });
        await this.payloadManager.emitPayloads(resultingPayloads, PayloadEmitSource.LocalChanged);
        const duplicate = this.itemManager.findSureItem(resultingPayloads[0].uuid);
        return duplicate;
    }
    async createItem(contentType, content, needsSync = false, vault) {
        const payload = new DecryptedPayload({
            uuid: UuidGenerator.GenerateUuid(),
            content_type: contentType,
            content: FillItemContent(content),
            dirty: needsSync,
            ...PayloadVaultOverrides(vault),
            ...PayloadTimestampDefaults(),
        });
        await this.payloadManager.emitPayload(payload, PayloadEmitSource.LocalInserted);
        return this.itemManager.findSureItem(payload.uuid);
    }
    async insertItem(item, setDirty = true) {
        const existingItem = this.itemManager.findItem(item.uuid);
        if (existingItem) {
            throw Error('Attempting to insert item that already exists');
        }
        if (setDirty) {
            const mutator = CreateDecryptedMutatorForItem(item, MutationType.UpdateUserTimestamps);
            const dirtiedPayload = mutator.getResult();
            const insertedItem = await this.emitItemFromPayload(dirtiedPayload, PayloadEmitSource.LocalInserted);
            return insertedItem;
        }
        else {
            return this.emitItemFromPayload(item.payload, PayloadEmitSource.LocalChanged);
        }
    }
    async insertItems(items, emitSource = PayloadEmitSource.LocalInserted) {
        return this.emitItemsFromPayloads(items.map((item) => item.payload), emitSource);
    }
    async emitItemFromPayload(payload, emitSource) {
        await this.payloadManager.emitPayload(payload, emitSource);
        const result = this.itemManager.findSureItem(payload.uuid);
        if (!result) {
            throw Error("Emitted item can't be found");
        }
        return result;
    }
    async emitItemsFromPayloads(payloads, emitSource) {
        await this.payloadManager.emitPayloads(payloads, emitSource);
        const uuids = Uuids(payloads);
        return this.itemManager.findItems(uuids);
    }
    async setItemToBeDeleted(itemToLookupUuidFor, source = PayloadEmitSource.LocalChanged) {
        const referencingIdsCapturedBeforeChanges = this.itemManager
            .getCollection()
            .uuidsThatReferenceUuid(itemToLookupUuidFor.uuid);
        const item = this.itemManager.findAnyItem(itemToLookupUuidFor.uuid);
        if (!item) {
            return;
        }
        const mutator = new DeleteItemMutator(item, MutationType.UpdateUserTimestamps);
        const deletedPayload = mutator.getDeletedResult();
        await this.payloadManager.emitPayload(deletedPayload, source);
        for (const referencingId of referencingIdsCapturedBeforeChanges) {
            const referencingItem = this.itemManager.findItem(referencingId);
            if (referencingItem) {
                await this.changeItem(referencingItem, (mutator) => {
                    mutator.removeItemAsRelationship(item);
                });
            }
        }
    }
    async setItemsToBeDeleted(itemsToLookupUuidsFor) {
        await Promise.all(itemsToLookupUuidsFor.map((item) => this.setItemToBeDeleted(item)));
    }
    async findOrCreateTagParentChain(titlesHierarchy) {
        let current = undefined;
        for (const title of titlesHierarchy) {
            current = await this.findOrCreateTagByTitle({ title, parentItemToLookupUuidFor: current });
        }
        if (!current) {
            throw new Error('Invalid tag hierarchy');
        }
        return current;
    }
    async createTag(dto) {
        const newTag = await this.createItem(ContentType.TYPES.Tag, FillItemContent({ title: dto.title }), true, dto.createInVault);
        if (dto.parentItemToLookupUuidFor) {
            const parentTag = this.itemManager.findItem(dto.parentItemToLookupUuidFor.uuid);
            if (!parentTag) {
                throw new Error('Invalid parent tag');
            }
            return this.changeTag(newTag, (m) => {
                m.makeChildOf(parentTag);
            });
        }
        return newTag;
    }
    async createSmartView(dto) {
        return this.createItem(ContentType.TYPES.SmartView, FillItemContent({
            title: dto.title,
            predicate: dto.predicate.toJson(),
            iconString: dto.iconString || SmartViewDefaultIconName,
        }), true, dto.vault);
    }
    async createSmartViewFromDSL(dsl, vault) {
        let components = null;
        try {
            components = JSON.parse(dsl.substring(1, dsl.length));
        }
        catch {
            throw Error('Invalid smart view syntax');
        }
        const title = components[0];
        const predicate = predicateFromDSLString(dsl);
        return this.createSmartView({ title, predicate, vault });
    }
    async createTagOrSmartView(title, vault) {
        if (this.itemManager.isSmartViewTitle(title)) {
            return this.createSmartViewFromDSL(title, vault);
        }
        else {
            return this.createTag({ title, createInVault: vault });
        }
    }
    async findOrCreateTagByTitle(dto) {
        const tag = this.itemManager.findTagByTitleAndParent(dto.title, dto.parentItemToLookupUuidFor);
        return tag || this.createTag(dto);
    }
    renameFile(file, name) {
        return this.changeItem(file, (mutator) => {
            mutator.name = name;
        });
    }
    async mergeItem(item, source) {
        return this.emitItemFromPayload(item.payloadRepresentation(), source);
    }
    async setItemNeedsSync(item, updateTimestamps = false) {
        return this.setItemDirty(item, updateTimestamps);
    }
    async setItemsNeedsSync(items) {
        return this.setItemsDirty(items);
    }
    async deleteItem(item) {
        return this.deleteItems([item]);
    }
    async deleteItems(items) {
        await this.setItemsToBeDeleted(items);
    }
    /**
     * Permanently deletes any items currently in the trash. Consumer must manually call sync.
     */
    async emptyTrash() {
        const notes = this.itemManager.trashedItems;
        await this.setItemsToBeDeleted(notes);
    }
    async migrateTagsToFolders() {
        await TagsToFoldersMigrationApplicator.run(this.itemManager, this);
    }
    async findOrCreateTag(title, createInVault) {
        return this.findOrCreateTagByTitle({ title, createInVault });
    }
    /**
     * @returns The changed child tag
     */
    async setTagParent(parentTag, childTag) {
        if (parentTag.uuid === childTag.uuid) {
            throw new Error('Can not set a tag parent of itself');
        }
        if (this.itemManager.isTagAncestor(childTag, parentTag)) {
            throw new Error('Can not set a tag ancestor of itself');
        }
        return this.changeTag(childTag, (m) => {
            m.makeChildOf(parentTag);
        });
    }
    /**
     * @returns The changed child tag
     */
    unsetTagParent(childTag) {
        const parentTag = this.itemManager.getTagParent(childTag);
        if (!parentTag) {
            return Promise.resolve(childTag);
        }
        return this.changeTag(childTag, (m) => {
            m.unsetParent();
        });
    }
    async associateFileWithNote(file, note) {
        const isVaultConflict = file.key_system_identifier &&
            note.key_system_identifier &&
            file.key_system_identifier !== note.key_system_identifier;
        if (isVaultConflict) {
            void this.alerts.alert(c('B4.Notes.TagsLinkedItems.Error')
                .t `The items you are trying to link belong to different vaults and cannot be linked`);
            return undefined;
        }
        return this.changeItem(file, (mutator) => {
            mutator.addNote(note);
        });
    }
    async disassociateFileWithNote(file, note) {
        return this.changeItem(file, (mutator) => {
            mutator.removeNote(note);
        });
    }
    async addTagToNote(note, tag, addHierarchy) {
        if (tag.key_system_identifier !== note.key_system_identifier) {
            void this.alerts.alert(c('B4.Notes.TagsLinkedItems.Error')
                .t `The items you are trying to link belong to different vaults and cannot be linked`);
            return undefined;
        }
        let tagsToAdd = [tag];
        if (addHierarchy) {
            const parentChainTags = this.itemManager.getTagParentChain(tag);
            tagsToAdd = [...parentChainTags, tag];
        }
        return Promise.all(tagsToAdd.map((tagToAdd) => {
            return this.changeTag(tagToAdd, (mutator) => {
                mutator.addNote(note);
            });
        }));
    }
    async addTagToFile(file, tag, addHierarchy) {
        if (tag.key_system_identifier !== file.key_system_identifier) {
            void this.alerts.alert(c('B4.Notes.TagsLinkedItems.Error')
                .t `The items you are trying to link belong to different vaults and cannot be linked`);
            return undefined;
        }
        let tagsToAdd = [tag];
        if (addHierarchy) {
            const parentChainTags = this.itemManager.getTagParentChain(tag);
            tagsToAdd = [...parentChainTags, tag];
        }
        return Promise.all(tagsToAdd.map((tagToAdd) => {
            return this.changeTag(tagToAdd, (mutator) => {
                mutator.addFile(file);
            });
        }));
    }
    async linkNoteToNote(note, otherNote) {
        return this.changeItem(note, (mutator) => {
            mutator.addNote(otherNote);
        });
    }
    async linkFileToFile(file, otherFile) {
        return this.changeItem(file, (mutator) => {
            mutator.addFile(otherFile);
        });
    }
    async unlinkItems(itemA, itemB) {
        const relationshipDirection = this.itemManager.relationshipDirectionBetweenItems(itemA, itemB);
        if (relationshipDirection === ItemRelationshipDirection.NoRelationship) {
            throw new Error('Trying to unlink already unlinked items');
        }
        const itemToChange = relationshipDirection === ItemRelationshipDirection.AReferencesB ? itemA : itemB;
        const itemToRemove = itemToChange === itemA ? itemB : itemA;
        return this.changeItem(itemToChange, (mutator) => {
            mutator.removeItemAsRelationship(itemToRemove);
        });
    }
}
